// src/door/components/FullscreenScanner.tsx
import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader, type IScannerControls } from "@zxing/browser";
import { getCameraStream, keepAlive, stopCamera } from "../utils/camera";

type Props = {
  onDetected: (text: string) => void;
  onClose: () => void;
};

export default function FullscreenScanner({ onDetected, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // флаги
  const closedRef   = useRef(false);
  const detectedRef = useRef(false);

  // BarcodeDetector
  const rafRef = useRef<number | null>(null);

  // ZXing
  const zxingRef  = useRef<BrowserMultiFormatReader | null>(null);
  const zxCtrlRef = useRef<IScannerControls | null>(null);

  const [hint, setHint] = useState<string>("");

  /** остановить только детекцию (без отключения камеры) */
  const stopDetection = () => {
    if (rafRef.current != null) {
      try { cancelAnimationFrame(rafRef.current); } catch {}
      rafRef.current = null;
    }
    try { zxCtrlRef.current?.stop?.(); } catch {}
    zxCtrlRef.current = null;
  };

  /** Полная остановка (и камеры тоже) — используем только при явном закрытии */
  const stopAll = () => {
    stopDetection();
    stopCamera();
    const v = videoRef.current;
    if (v) {
      try { v.pause(); } catch {}
      v.srcObject = null;
      v.removeAttribute("src");
    }
  };

  /** BarcodeDetector (работаем поверх уже открытого потока, без нового getUserMedia) */
  const startBarcodeDetector = async () => {
    setHint("Запуск камеры…");
    const stream = await getCameraStream({
      facingMode: { ideal: "environment" },
      width:  { ideal: 1280, min: 640 },
      height: { ideal: 720,  min: 360 },
    });
    const v = videoRef.current!;
    if (v.srcObject !== stream) v.srcObject = stream;
    await v.play().catch(() => {});

    // @ts-ignore
    const BD: any = (window as any).BarcodeDetector;
    const detector = new BD({ formats: ["qr_code"] });

    const loop = async () => {
      if (closedRef.current) return;
      try {
        const bmp = await createImageBitmap(v);
        const codes = await detector.detect(bmp);
        // @ts-ignore
        bmp.close?.();
        if (codes?.length) {
          const raw = codes[0].rawValue || codes[0].rawText || "";
          if (raw) {
            detectedRef.current = true;
            keepAlive(12000);        // время на «Сканировать следующего» без нового запроса
            stopDetection();         // камеру не выключаем
            onDetected(String(raw));
            return;
          }
        }
      } catch {}
      rafRef.current = requestAnimationFrame(loop);
    };

    setHint("");
    rafRef.current = requestAnimationFrame(loop);
  };

  /** ZXing из уже присоединённого video (НЕ вызывает getUserMedia повторно) */
  const startZxing = async () => {
    setHint("Запуск сканера…");
    if (!zxingRef.current) zxingRef.current = new BrowserMultiFormatReader();

    const stream = await getCameraStream({
      facingMode: { ideal: "environment" },
      width:  { ideal: 1280, min: 640 },
      height: { ideal: 720,  min: 360 },
    });
    const v = videoRef.current!;
    if (v.srcObject !== stream) v.srcObject = stream;
    await v.play().catch(() => {});

    zxCtrlRef.current = await zxingRef.current.decodeFromVideoElement(
      videoRef.current!,
      (result, _err, controls) => {
        if (!result) return;
        const text = result.getText();
        if (!text) return;
        detectedRef.current = true;
        try { controls.stop(); } catch {}
        zxCtrlRef.current = null;
        keepAlive(12000);
        onDetected(text);
      }
    );
    setHint("");
  };

  useEffect(() => {
    closedRef.current = false;
    detectedRef.current = false;

    (async () => {
      try {
        // строго один путь
        const BD: any = (window as any).BarcodeDetector;
        const supported: string[] | undefined = BD && (await BD.getSupportedFormats?.());
        if (BD && supported?.includes("qr_code")) {
          await startBarcodeDetector();
        } else {
          await startZxing();
        }
      } catch (e: any) {
        // fallback → ZXing через video
        try {
          await startZxing();
        } catch {
          setHint(e?.name || "camera_error");
        }
      }
    })();

    // при размонтировании после успешного скана камеру НЕ гасим —
    // гасим только при явном закрытии.
    return () => {
      stopDetection();
      if (closedRef.current && !detectedRef.current) {
        stopAll();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDetected]);

  const handleClose = () => {
    closedRef.current = true;
    stopAll();   // явное закрытие → выключаем
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80">
      {/* ВИДЕО: без blur, без апскейла — object-contain */}
      <video
        ref={videoRef}
        className="absolute inset-0 z-0 h-full w-full object-contain"
        playsInline
        autoPlay
        muted
      />

      {/* РАМКА + затемнение без backdrop-blur: делаем «маску» большой тенью */}
      <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
        <div className="relative w-[78vw] max-w-[540px] aspect-square">
          {/* тень вокруг как маска */}
          <div className="absolute inset-0 rounded-[28px] shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
          <div className="absolute inset-0 rounded-[28px] ring-2 ring-white/85" />
          <div className="absolute left-[8%] right-[8%] top-[12%] h-[2px] bg-white/90 animate-scan" />
        </div>
      </div>

      {/* Кнопка закрытия и подсказка */}
      <div className="absolute inset-x-0 bottom-0 z-30 p-4">
        {hint && (
          <div className="mx-auto mb-3 max-w-[320px] rounded-xl bg-white/10 px-4 py-2 text-center text-sm text-white/90">
            {hint}
          </div>
        )}
        <div className="mx-auto flex max-w-[360px] gap-8">
          <button
            onClick={handleClose}
            className="flex-1 rounded-full bg-white/10 px-5 py-3 text-white hover:bg-white/15 active:translate-y-[1px]"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}